import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Flame, Zap, Users, Clock, Mountain } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BurnScale = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();

  const burnLevels = [
    {
      level: 1,
      name: 'The Socialite',
      name_es: 'El Socialité',
      tagline: 'Culture, cocktails, soft adventure',
      tagline_es: 'Cultura, cócteles, aventura suave',
      description: 'Light biking, boat rides, hidden beaches. Party hard, adventure light.',
      description_es: 'Ciclismo ligero, paseos en bote, playas escondidas. Fiesta intensa, aventura suave.',
      effortHours: '2-4',
      icon: Sparkles,
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      activities: ['Beach clubs', 'City tours', 'Food & wine', 'Light hikes'],
      activities_es: ['Clubes de playa', 'Tours urbanos', 'Gastronomía y vino', 'Caminatas ligeras'],
    },
    {
      level: 2,
      name: 'The Vibe',
      name_es: 'La Vibra',
      tagline: 'Dirt on your boots, volcano hiking',
      tagline_es: 'Tierra en tus botas, senderismo volcánico',
      description: 'Moderate cycling, river rafting, day treks. The sweet spot of challenge and reward.',
      description_es: 'Ciclismo moderado, rafting, caminatas de día. El punto perfecto entre desafío y recompensa.',
      effortHours: '4-6',
      icon: Flame,
      color: 'from-orange-500 to-amber-500',
      bgColor: 'bg-orange-500/10',
      borderColor: 'border-orange-500/30',
      activities: ['Volcano hikes', 'Surfing', 'Rafting', 'Cultural immersion'],
      activities_es: ['Senderismo en volcanes', 'Surf', 'Rafting', 'Inmersión cultural'],
    },
    {
      level: 3,
      name: 'The Badass',
      name_es: 'El Temerario',
      tagline: 'Total immersion, no signal, no shortcuts',
      tagline_es: 'Inmersión total, sin señal, sin atajos',
      description: 'Multi-day treks, whitewater, high-altitude. For those who want to be transformed.',
      description_es: 'Caminatas de varios días, rápidos, altura. Para quienes quieren transformarse.',
      effortHours: '6-8+',
      icon: Zap,
      color: 'from-red-500 to-orange-600',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
      activities: ['Multi-day treks', 'Glacier hikes', 'High altitude', 'Wilderness camping'],
      activities_es: ['Trekking multi-día', 'Glaciares', 'Alta montaña', 'Camping salvaje'],
    },
  ];

  return (
    <section className="py-20 px-4 bg-white" data-testid="burn-scale-section">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-[#ffb84d]/20 text-[#ffb84d] text-sm font-bold rounded-full mb-4">
            {language === 'es' ? 'LA ESCALA DE INTENSIDAD' : 'THE BURN SCALE'}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 mb-4">
            {language === 'es' ? '¿Cuánta intensidad buscas?' : 'How hard do you want to go?'}
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            {language === 'es' 
              ? 'Cada viaje tiene un nivel de intensidad. Encuentra el tuyo.' 
              : 'Every trip has an intensity level. Find yours.'}
          </p>
        </div>

        {/* Burn Level Cards */}
        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
          {burnLevels.map((burn, index) => {
            const IconComponent = burn.icon;
            return (
              <div
                key={burn.level}
                data-testid={`burn-level-${burn.level}`}
                className={`
                  relative overflow-hidden rounded-2xl border-2 ${burn.borderColor} ${burn.bgColor}
                  p-6 lg:p-8 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl
                  group cursor-pointer
                `}
                onClick={() => navigate(`/trips?burn_level=${burn.level}`)}
              >
                {/* Level Badge */}
                <div className={`
                  absolute top-4 right-4 w-12 h-12 rounded-full bg-gradient-to-br ${burn.color}
                  flex items-center justify-center shadow-lg
                `}>
                  <span className="text-white font-black text-lg">{burn.level}</span>
                </div>

                {/* Icon */}
                <div className={`
                  w-16 h-16 rounded-2xl bg-gradient-to-br ${burn.color}
                  flex items-center justify-center mb-6 group-hover:scale-110 transition-transform
                `}>
                  <IconComponent className="w-8 h-8 text-white" />
                </div>

                {/* Content */}
                <h3 className="text-2xl font-black text-gray-900 mb-2">
                  {language === 'es' ? burn.name_es : burn.name}
                </h3>
                <p className={`text-sm font-bold bg-gradient-to-r ${burn.color} bg-clip-text text-transparent mb-4`}>
                  {language === 'es' ? burn.tagline_es : burn.tagline}
                </p>
                <p className="text-gray-600 mb-6">
                  {language === 'es' ? burn.description_es : burn.description}
                </p>

                {/* Stats */}
                <div className="flex items-center gap-4 mb-6 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    <span>{burn.effortHours} {language === 'es' ? 'hrs/día' : 'hrs/day'}</span>
                  </div>
                </div>

                {/* Activities */}
                <div className="flex flex-wrap gap-2">
                  {(language === 'es' ? burn.activities_es : burn.activities).map((activity, i) => (
                    <span 
                      key={i}
                      className="px-3 py-1 bg-white/50 text-gray-700 text-xs font-medium rounded-full"
                    >
                      {activity}
                    </span>
                  ))}
                </div>

                {/* Hover Arrow */}
                <div className="absolute bottom-6 right-6 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${burn.color} flex items-center justify-center`}>
                    <span className="text-white">→</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BurnScale;
